import { ArtistJobs, FlightPaymentType, Role } from '@customTypes/types';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useCreateWorkInviteMutation,
  useCreateWorkRequestMutation
} from '@/features/work/api';
import { interactionWorkItemsStatusFilters } from '@/features/work-interactions/constants';
import { VALIDATE } from '@/shared/constants/validation';
import { useProfileStore } from '@/shared/store/profileStore';

import { useShowFullClubProfileQuery } from '../../api/show-full-club-profile';

export const useCreateWorkItemForm = ({
  artistProfileId,
  clubProfileId,
  onSubmittingSuccess,
  workItemType
}: {
  artistProfileId: number;
  clubProfileId: number;
  workItemType: 'request' | 'invite';
  onSubmittingSuccess?: () => void;
}) => {
  const createWorkInviteMutation = useCreateWorkInviteMutation();
  const queryClient = useQueryClient();
  const { profile } = useProfileStore();
  const { formatMessage } = useIntl();
  const createWorkRequestMutation = useCreateWorkRequestMutation();

  const clubFullProfile = useShowFullClubProfileQuery(
    {
      profileId: clubProfileId.toString()
    },
    {
      options: {
        enabled: profile?.role === Role.artist
      }
    }
  );

  const form = useForm<{
    artistProfileId: number;
    clubProfileId: number;
    jobRole: ArtistJobs;
    startDate: string | null;
    endDate: string | null;
    ticketCreditation: boolean;
  }>({
    initialValues: {
      artistProfileId,
      clubProfileId,
      jobRole: ArtistJobs.Hostess,
      startDate: null,
      endDate: null,
      ticketCreditation: false
    },
    validate: zodResolver(
      z.object({
        startDate: VALIDATE.DATE_REQUIRED,
        endDate: VALIDATE.DATE_REQUIRED
      })
    )
  });

  useEffect(() => {
    form.setFieldValue(
      'ticketCreditation',
      clubFullProfile.data?.data.contractTerms.flightPaymentType ===
        FlightPaymentType.Credit
    );
  }, [clubFullProfile.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    if (
      workItemType === 'invite' &&
      values.startDate !== null &&
      values.endDate !== null
    ) {
      return createWorkInviteMutation.mutateAsync(
        {
          params: {
            ...values,
            endDate: values.endDate,
            startDate: values.startDate
          }
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['ClubWorkInvitesListQuery']
            });

            queryClient.invalidateQueries({
              queryKey: [
                'ClubWorkItemsList',
                clubProfileId.toString(),
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });
            notifications.show({
              title: formatMessage({
                id: 'notifications.waitResponseIn24Hours'
              }),
              ...notificationStyleConfig
            });
            if (onSubmittingSuccess) {
              onSubmittingSuccess();
            }
          },
          onError: () => {
            if (onSubmittingSuccess) {
              onSubmittingSuccess();
            }
          }
        }
      );
    }

    if (values.startDate !== null && values.endDate !== null) {
      await createWorkRequestMutation.mutateAsync(
        {
          params: {
            ...values,
            endDate: values.endDate,
            startDate: values.startDate
          }
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['ArtistWorkRequestsList']
            });

            queryClient.invalidateQueries({
              queryKey: [
                'ArtistWorkItemsList',
                artistProfileId.toString(),
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });

            notifications.show({
              title: formatMessage({
                id: 'notifications.waitResponseIn24Hours'
              }),
              ...notificationStyleConfig
            });

            if (onSubmittingSuccess) {
              onSubmittingSuccess();
            }
          },
          onError: () => {
            if (onSubmittingSuccess) {
              onSubmittingSuccess();
            }
          }
        }
      );
    }
  });

  return {
    state: {
      form,
      clubProfile: clubFullProfile.data?.data
    },
    functions: { onSubmit }
  };
};
